import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { MouseEvent, useEffect, useId, useRef, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';

import IconButton from '_components/UI/IconButton';
import SectionAccordion from '_components/UI/SectionAccordion';
import TextButton from '_components/UI/TextButton';
import FacetRefinement from '@components/algolia/SearchResults/facets/FacetRefinement';
import { getFacetTitle } from '@components/algolia/SearchResults/facets/getFacetTitle';
import { useFilters } from '@components/algolia/SearchResults/FiltersProvider';
import { useEventListener } from '@hooks/useEventListener';

import styles from './AllFiltersPanel.module.scss';

const AllFiltersPanel = () => {
  const { t } = useTranslation(['searchResults']);
  const {
    orderedFacets,
    isFiltersPanelOpen,
    closeFiltersPanel,
    getRefinementCount,
    totalRefinementCount,
    locale,
  } = useFilters();

  const panelRef = useRef<HTMLElement>(null);
  const documentRef = useRef<Document | null>(null);
  const titleId = useId();

  // Each facet accordion opens by default when it has a selection, but while
  // the panel is open its open/closed state is user-controlled (via
  // onStateChange below) rather than tied to the live count. Otherwise
  // unchecking a facet's last selected option (count drops to 0) would force
  // the accordion shut while the customer is still looking at it.
  //
  // Reseeded from live counts on every closed -> open transition (not on
  // every render) rather than once ever: the panel stays mounted for the
  // page's lifetime, so without this, refining a facet through a different
  // surface (e.g. a FilterBar pill) while the panel is closed would leave
  // its accordion collapsed the next time the panel opens, even though it
  // now has an active selection.
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isFiltersPanelOpen) return;
    setOpenFacets(
      Object.fromEntries(
        orderedFacets.map((attribute) => [
          attribute,
          getRefinementCount(attribute) > 0,
        ])
      )
    );
    // Deliberately reseed only on the closed -> open transition — reacting
    // to orderedFacets/getRefinementCount changes while already open would
    // reintroduce the bug this state exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiltersPanelOpen]);

  useEffect(() => {
    documentRef.current = document;
  }, []);

  useEffect(() => {
    // assigns focus for accessibility and prevents scrolling in background
    if (isFiltersPanelOpen) {
      panelRef.current?.focus();
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'auto';
    }
    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [isFiltersPanelOpen]);

  const handleEscapeKey = (e: KeyboardEvent) => {
    // A SectionAccordion consumes Escape (preventDefault) to close itself
    // first; only an unhandled Escape closes the whole panel.
    if (isFiltersPanelOpen && e.key === 'Escape' && !e.defaultPrevented) {
      closeFiltersPanel();
    }
  };

  useEventListener('keydown', handleEscapeKey, documentRef);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    // Only a direct click on the backdrop itself should close the panel.
    // Checking DOM containment of the panel is unreliable here: facet
    // controls like DropdownMenu render their popup via a portal, so their
    // DOM node isn't a descendant of panelRef even though the click event
    // still bubbles up to this handler through the React tree.
    if (e.target === e.currentTarget) {
      closeFiltersPanel();
    }
  };

  const hasSelections = totalRefinementCount > 0;

  // The subcopy always stays mounted (so its fade can animate); once
  // selections drop to 0 this keeps showing the last real count through the
  // fade-out instead of snapping to "0 filters selected" first.
  const [lastNonZeroCount, setLastNonZeroCount] =
    useState(totalRefinementCount);
  useEffect(() => {
    if (hasSelections) {
      setLastNonZeroCount(totalRefinementCount);
    }
  }, [hasSelections, totalRefinementCount]);

  const displayedCount = hasSelections
    ? totalRefinementCount
    : lastNonZeroCount;
  const selectedCountLabel =
    displayedCount === 1
      ? t('search.facets.selectedCount', { count: displayedCount })
      : t('search.facets.selectedCount.plural', { count: displayedCount });

  return (
    <div
      className={cn(styles.container, {
        [styles.containerOpen]: isFiltersPanelOpen,
      })}
      onClick={handleBackdropClick}
    >
      <section
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div
            className={cn(styles.headerTitle, {
              [styles.hasSelections]: hasSelections,
            })}
          >
            <h2 className={styles.title} id={titleId}>
              {t('search.facets.title')}
            </h2>
            <p className={styles.selectedCount} aria-hidden={!hasSelections}>
              {selectedCountLabel}
            </p>
          </div>
          <IconButton
            icon={XIcon}
            type="outline"
            size="xl"
            iconSize={16}
            iconWeight="bold"
            className={styles.closeButton}
            aria-label={t('search.facets.close')}
            onClick={closeFiltersPanel}
          />
        </header>

        <div className={styles.facets}>
          {orderedFacets.map((attribute) => {
            const count = getRefinementCount(attribute);
            const title = getFacetTitle(attribute, t);

            return (
              <SectionAccordion
                key={attribute}
                icon="plus"
                size="small"
                isOpen={openFacets[attribute] ?? count > 0}
                onStateChange={(state) =>
                  setOpenFacets((prev) => ({
                    ...prev,
                    [attribute]: state === 'open',
                  }))
                }
                className={styles.facetAccordion}
              >
                <SectionAccordion.Header
                  title={count > 0 ? `${title} (${count})` : title}
                  titleClassName={styles.facetTitle}
                />
                <SectionAccordion.Collapsible>
                  <div className={styles.facetContent}>
                    <FacetRefinement
                      attribute={attribute}
                      locale={locale}
                      onRequestClose={closeFiltersPanel}
                      context="allFilters"
                      hasActiveRefinement={count > 0}
                    />
                  </div>
                </SectionAccordion.Collapsible>
              </SectionAccordion>
            );
          })}
        </div>

        <div className={styles.ctaBar}>
          <TextButton
            type="primary"
            label={t('search.facets.apply')}
            disabled={totalRefinementCount === 0}
            className={styles.applyButton}
            onClick={closeFiltersPanel}
          />
        </div>
      </section>
    </div>
  );
};

export default AllFiltersPanel;
